import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Heart, Shield, CheckCircle, CreditCard, Target, ArrowLeft } from "lucide-react";

const DONATION_AMOUNTS = [25, 50, 100, 250, 500, 1000];

export default function DonatePage() {
  const [searchParams] = useSearchParams();
  const campaignId = searchParams.get('campaign');
  
  const [campaign, setCampaign] = useState(null);
  const [loadingCampaign, setLoadingCampaign] = useState(false);
  const [amount, setAmount] = useState(100);
  const [customAmount, setCustomAmount] = useState("");
  const [recurring, setRecurring] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    dedication: "",
    anonymous: false
  });
  const [processing, setProcessing] = useState(false);

  // Fetch campaign details if campaignId is provided
  useEffect(() => {
    if (campaignId) {
      fetchCampaign();
    }
  }, [campaignId]);

  const fetchCampaign = async () => {
    setLoadingCampaign(true);
    try {
      const response = await axios.get(`${API}/campaigns/${campaignId}`);
      setCampaign(response.data);
    } catch (error) {
      console.error("Error fetching campaign:", error);
      toast.error("Campaign not found");
    } finally {
      setLoadingCampaign(false);
    }
  };

  const handleAmountSelect = (value) => {
    setAmount(value);
    setCustomAmount("");
  };

  const handleCustomAmountChange = (e) => {
    const value = e.target.value;
    setCustomAmount(value);
    if (value) {
      setAmount(parseFloat(value) || 0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email) {
      toast.error("Please fill in your name and email");
      return;
    }
    
    if (amount < 1) {
      toast.error("Please enter a valid donation amount");
      return;
    }

    setProcessing(true);
    try {
      const response = await axios.post(`${API}/donations/checkout`, {
        amount,
        recurring,
        donor_name: formData.name,
        donor_email: formData.email,
        dedication: formData.dedication,
        anonymous: formData.anonymous,
        campaign: campaign?.title || campaignId || "general",
        campaign_id: campaignId || "",
        campaign_title: campaign?.title || null
      }, {
        headers: { origin: window.location.origin }
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error("Error creating donation:", error);
      toast.error(error.response?.data?.detail || "Failed to process donation");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Layout>
      {/* Campaign Banner if donating to specific campaign */}
      {campaign && (
        <section className="bg-awareness-blue/10 py-4 border-b border-awareness-blue/20">
          <div className="container-custom">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Target className="w-5 h-5 text-deep-blue" />
                <span className="text-deep-blue font-medium">
                  Donating to: <strong>{campaign.title}</strong>
                </span>
              </div>
              <Link to="/campaigns" className="text-sm text-deep-blue hover:underline flex items-center gap-1">
                <ArrowLeft className="w-4 h-4" />
                View all campaigns
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Hero Section */}
      <section className="gradient-hero py-16" data-testid="donate-hero">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="text-awareness-blue font-semibold text-sm uppercase tracking-wide">Make A Difference</span>
            <h1 className="text-heading-1 text-white mt-4 mb-4">Support Our Mission</h1>
            <p className="text-white/80 text-lg">
              Your donation helps us spread prostate cancer awareness to first responders and 
              communities across America. Every dollar makes a difference.
            </p>
          </div>
        </div>
      </section>

      <section className="section-spacing">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Donation Form */}
            <div data-testid="donation-form">
              <div className="bg-white border border-slate-200 rounded-lg p-8">
                <h2 className="text-heading-2 text-deep-blue mb-6">Make a Donation</h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Amount Selection */}
                  <div>
                    <Label className="text-base font-semibold mb-3 block">Select Amount</Label>
                    <div className="grid grid-cols-3 gap-3 mb-3">
                      {DONATION_AMOUNTS.map((value) => (
                        <Button
                          key={value}
                          type="button"
                          variant={amount === value && !customAmount ? "default" : "outline"}
                          className={amount === value && !customAmount ? "btn-primary" : ""}
                          onClick={() => handleAmountSelect(value)}
                          data-testid={`amount-${value}`}
                        >
                          ${value}
                        </Button>
                      ))}
                    </div>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                      <Input 
                        type="number"
                        placeholder="Custom amount"
                        value={customAmount}
                        onChange={handleCustomAmountChange}
                        className="pl-8"
                        min="1"
                        data-testid="custom-amount"
                      />
                    </div>
                  </div>

                  {/* Recurring Option */}
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="recurring" 
                      checked={recurring}
                      onCheckedChange={setRecurring}
                      data-testid="recurring-checkbox"
                    />
                    <Label htmlFor="recurring" className="cursor-pointer">
                      Make this a monthly recurring donation
                    </Label>
                  </div>

                  {/* Donor Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-deep-blue">Your Information</h3>
                    
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input 
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        placeholder="John Doe"
                        required
                        data-testid="donor-name"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input 
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder="john@example.com"
                        required
                        data-testid="donor-email"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="dedication">In Honor/Memory Of (Optional)</Label>
                      <Input 
                        id="dedication"
                        value={formData.dedication}
                        onChange={(e) => setFormData({...formData, dedication: e.target.value})}
                        placeholder="In honor of..."
                        data-testid="dedication"
                      />
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="anonymous" 
                        checked={formData.anonymous}
                        onCheckedChange={(checked) => setFormData({...formData, anonymous: checked})}
                        data-testid="anonymous-checkbox"
                      />
                      <Label htmlFor="anonymous" className="cursor-pointer">
                        Make my donation anonymous
                      </Label>
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">
                        {recurring ? "Monthly Donation" : "One-Time Donation"}
                      </span>
                      <span className="text-2xl font-bold text-deep-blue">
                        ${amount.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full btn-primary text-lg py-4"
                    disabled={processing}
                    data-testid="donate-submit"
                  >
                    {processing ? (
                      "Processing..."
                    ) : (
                      <>
                        <CreditCard className="w-5 h-5 mr-2" />
                        Donate ${amount.toFixed(2)} {recurring && "Monthly"}
                      </>
                    )}
                  </Button>

                  <p className="text-center text-sm text-slate-500">
                    Secure payment powered by Stripe
                  </p>
                </form>
              </div>
            </div>

            {/* Impact Information */}
            <div className="space-y-8" data-testid="donation-impact">
              <div>
                <h2 className="text-heading-2 text-deep-blue mb-4">Your Impact</h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Your generous donation directly supports our mission to bring prostate cancer 
                  awareness to first responders and communities across the United States.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-4 bg-slate-50 p-4 rounded-lg">
                  <div className="w-10 h-10 bg-awareness-blue/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-awareness-blue" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-deep-blue">$25 - Awareness Materials</h4>
                    <p className="text-sm text-slate-600">Provides educational brochures for 50 first responders</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 bg-slate-50 p-4 rounded-lg">
                  <div className="w-10 h-10 bg-awareness-blue/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-awareness-blue" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-deep-blue">$100 - Community Event</h4>
                    <p className="text-sm text-slate-600">Sponsors one community awareness event</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 bg-slate-50 p-4 rounded-lg">
                  <div className="w-10 h-10 bg-awareness-blue/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-awareness-blue" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-deep-blue">$500 - Screening Program</h4>
                    <p className="text-sm text-slate-600">Funds free screening services for a community</p>
                  </div>
                </div>
              </div>

              {/* Trust Badges */}
              <div className="bg-deep-blue text-white p-6 rounded-lg">
                <div className="flex items-center gap-3 mb-4">
                  <Shield className="w-8 h-8 text-awareness-blue" />
                  <div>
                    <h4 className="font-bold text-lg">501(c)(3) Tax-Exempt</h4>
                    <p className="text-white/80 text-sm">Your donation is tax-deductible</p>
                  </div>
                </div>
                <p className="text-white/70 text-sm">
                  Blue vs Blue is a registered 501(c)(3) nonprofit organization. Your donation 
                  is tax-deductible to the fullest extent allowed by law.
                </p>
              </div>

              {/* Other Ways to Give */}
              <div>
                <h3 className="text-lg font-semibold text-deep-blue mb-3">Other Ways to Support</h3>
                <ul className="space-y-2 text-slate-600">
                  <li className="flex items-center gap-2">
                    <Heart className="w-4 h-4 text-awareness-blue" />
                    <Link to="/get-involved" className="hover:text-deep-blue">Volunteer your time</Link>
                  </li>
                  <li className="flex items-center gap-2">
                    <Heart className="w-4 h-4 text-awareness-blue" />
                    <Link to="/events" className="hover:text-deep-blue">Attend our events</Link>
                  </li>
                  <li className="flex items-center gap-2">
                    <Heart className="w-4 h-4 text-awareness-blue" />
                    <Link to="/get-involved" className="hover:text-deep-blue">Become a sponsor</Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
